import Header from '../components/Header';
import Footer from '../components/Footer';
import { useEffect, useState, useCallback, memo } from 'react';
import { db } from '../config/Firebase';
import { collection, getDocs, doc, getDoc, query, limit } from 'firebase/firestore';

const productCollections = [
  'DesignerMetal&MetalFrame',
  'Accessories',
  'FrameLess',
  'FullFrame',
  'HalfFrame',
  'Kids-Frame',
  'Kids-Sunglass',
  'LDB',
  'SafetyGlassess',
  'Sunglassess',
  'Types',
];

// Memoized card for performance
const OrderCard = memo(({ order, index, productImages }) => {
  const timelineStages = [
    { status: 'Order Confirmed', key: 'confirmed' },
    { status: 'Order Shipped', key: 'shipped' },
    { status: 'Order Delivered', key: 'delivered' },
  ];

  // Determine current status index
  const getStatusIndex = (status) => {
    const key = status?.toLowerCase() || '';
    if (key.includes('delivered')) return 2;
    if (key.includes('shipped') || key.includes('shipping')) return 1;
    return 0;
  };

  const currentStatus = order.products?.[0]?.status;
  const statusIndex = getStatusIndex(currentStatus);

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition p-4 sm:p-6">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h2 className="text-xl sm:text-2xl font-semibold text-black">Order #{index + 1}</h2>
        <span className="text-gray-500 text-xs sm:text-sm">
          {order.timestamp instanceof Object
            ? order.timestamp.toDate().toLocaleDateString()
            : order.timestamp || 'N/A'}
        </span>
      </div>

      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6 text-gray-700 text-xs sm:text-sm">
        <p><span className="font-medium text-black">Total:</span> ${order.totalAmount || '0.00'}</p>
        <p><span className="font-medium text-black">Items:</span> {order.products.length}</p>
      </div>

      <section className="mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-medium text-black mb-2">Customer Info</h3>
        <div className="text-gray-600 space-y-1 text-xs sm:text-sm">
          <p><span className="font-medium text-black">Name:</span> {order.customerDetails?.name || 'N/A'}</p>
          <p><span className="font-medium text-black">Address:</span> {order.customerDetails?.address || 'N/A'}</p>
          <p><span className="font-medium text-black">Phone:</span> {order.customerDetails?.phone || 'N/A'}</p>
        </div>
      </section>

      {order.lensOption && (
        <section className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-medium text-black mb-2">Lens Options</h3>
          <div className="text-gray-600 space-y-1 text-xs sm:text-sm">
            <p><span className="font-medium text-black">Type:</span> {order.lensOption.name}</p>
            <p><span className="font-medium text-black">Lens Option:</span> {order.lensOption.lensOptions}</p>
            <p><span className="font-medium text-black">Left Power:</span> {order.lensOption.leftLensPower}</p>
            <p><span className="font-medium text-black">Right Power:</span> {order.lensOption.rightLensPower}</p>
          </div>
        </section>
      )}

      <section className="mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-medium text-black mb-2">Products</h3>
        <div className="space-y-4">
          {order.products.map((product, idx) => {
            const pid = product.productID || product.productId;
            const img = pid ? productImages[pid] : null;
            return (
              <div key={idx} className="flex bg-gray-50 p-3 sm:p-4 rounded-lg">
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 mr-3 sm:mr-4">
                  {img ? (
                    <img
                      src={img}
                      alt={product.name}
                      className="w-full h-full object-contain rounded-md"
                      loading="lazy"
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/150')}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-md text-gray-500 text-xs sm:text-sm">
                      No Image
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-base sm:text-lg font-semibold text-black mb-1">{product.name || 'Unnamed'}</h4>
                  <div className="text-gray-600 text-xs sm:text-sm">
                    <p><span className="font-medium text-black">Price:</span> ${product.cost || '0.00'}</p>
                    <p><span className="font-medium text-black">Color:</span> {product.frameColor || 'N/A'}</p>
                    <p><span className="font-medium text-black">Material:</span> {product.material || 'N/A'}</p>
                    <p><span className="font-medium text-black">Shape:</span> {product.shape || 'N/A'}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="text-base sm:text-lg font-medium text-black mb-2">Order Status</h3>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-1 bg-gray-200" />
          </div>
          <div className="relative flex justify-between">
            {timelineStages.map((step, i) => (
              <div key={step.key} className="flex flex-col items-center">
                <div
                  className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 transition-all text-xs sm:text-base ${
                    i <= statusIndex
                      ? 'bg-black text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i + 1}
                </div>
                <p className={`text-[10px] sm:text-xs ${i <= statusIndex ? 'text-black' : 'text-gray-500'}`}>
                  {step.status}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
});

export default function UserOrder() {
  const [orders, setOrders] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [error, setError] = useState(null);

  // Check user email from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) throw new Error('No user');
      const user = JSON.parse(stored);
      if (user?.email) setUserEmail(user.email);
      else throw new Error('Email missing');
    } catch {
      setError('Please log in to view your orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch product images helper
  const fetchProductImages = useCallback(async (normalized) => {
    const ids = [...new Set(normalized.flatMap(o => o.products.map(p => p.productID || p.productId)).filter(Boolean))];
    const imgMap = {};
    await Promise.all(
      productCollections.map(async (col) => {
        await Promise.all(
          ids.map(async (id) => {
            try {
              const ref = doc(db, col, id);
              const snap = await getDoc(ref);
              if (snap.exists()) {
                const d = snap.data();
                const url = [d.ImageUrl1, d.ImageUrl2, d.ImageUrl3, d.ImageUrl4, d.imageUrl, d.image].find(u => u);
                if (url && !imgMap[id]) imgMap[id] = url;
              }
            } catch {}
          })
        );
      })
    );
    setProductImages(imgMap);
  }, []);

  // Fetch orders logic
  const fetchOrders = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError(null);
    try {
      const ref = doc(db, 'detailsorder', userEmail);
      const snap = await getDoc(ref);
      let dataArr = [];
      if (snap.exists()) {
        const d = snap.data();
        dataArr = Array.isArray(d.orders) ? d.orders : [d.orders].filter(Boolean);
      } else {
        const colRef = collection(db, 'users', userEmail, 'orders');
        const altSnap = await Promise.race([
          getDocs(query(colRef, limit(10))),
          new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout')), 10000)),
        ]);
        dataArr = altSnap.docs.map(doc => doc.data());
      }
      const normalized = dataArr.map(o => ({
        ...o,
        products: Array.isArray(o.products)
          ? o.products
          : o.productDetails
            ? [o.productDetails]
            : [o.products || {}]
      }));
      setOrders(normalized);
      await fetchProductImages(normalized);
    } catch {
      setError('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [userEmail, fetchProductImages]);

  // Trigger fetch
  useEffect(() => {
    if (userEmail) fetchOrders();
  }, [userEmail, fetchOrders]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <p className="text-base sm:text-lg text-gray-500 animate-pulse">Loading orders...</p>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8 sm:py-12">
        {error ? (
          <div className="bg-gray-100 border border-gray-300 text-black p-4 sm:p-6 rounded-lg text-center text-sm sm:text-base">
            {error}
          </div>
        ) : (
          <>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-black text-center mb-8 sm:mb-12">My Orders</h1>
            {orders.length === 0 ? (
              <div className="text-center text-gray-600 text-sm sm:text-base">
                <p className="mb-2">No orders found.</p>
                <p>If you just placed an order, it may take a moment to appear.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:gap-8">
                {orders.map((o, i) => <OrderCard key={i} order={o} index={i} productImages={productImages} />)}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}